import { useEffect, useState, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Plus,
  Layers,
  Info,
  Lock,
  AlertTriangle,
  Cpu,
  UserCog,
  Wrench,
  ArrowLeft,
  Github,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { EditableCommandList } from '@reflex/ui/components/content/EditableCommandList';
import { EditableCommand } from '@reflex/ui/components/content/EditableCommand';
import { FieldGroup } from '@reflex/ui/components/forms/FieldGroup';
import { cn } from '@reflex/ui/lib/utils';
import { FormField } from '@/components/forms/FormField';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { PanelTitle, PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { ConfigChipField } from '@reflex/ui/components/agent/ConfigChip';
import {
  ConfigChipSelect,
  type ConfigChipSelectOption,
} from '@reflex/ui/components/agent/ConfigChipSelect';
import { DevboxSizeChip } from '@/components/agent/DevboxSizeChip';
import { CollapsibleConfigSection } from '@/components/shared/CollapsibleConfigSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import {
  KeyValueEditor,
  type KeyValueEntry,
  type KeyValueEditorHandle,
} from '@reflex/ui/components/forms/KeyValueEditor';
import { FileMountsEditor, type FileMountsEditorHandle } from '@/components/forms/FileMountsEditor';
import {
  BlueprintBuildSecretsEditor,
  type BlueprintBuildSecret,
} from '@/components/blueprint/BlueprintBuildSecretsEditor';
import { useBlueprints, useCreateBlueprint } from '@/hooks/blueprints/useBlueprints';
import { useBaseImage } from '@/hooks/blueprints/useBaseImage';
import { RepoSelector } from '@reflex/plugin-github/web/RepoSelector';
import {
  useGitHubRepos,
  deriveGitHubConnectionState,
} from '@reflex/plugin-github/web/useGitHubRepos';
import { getApiUrl } from '@reflex/ui/client/http';
import {
  getLatestByName,
  deriveBlueprintNameFromRepo,
  type BlueprintPrefillRepoState,
} from '@/lib/blueprints';
import { isBaseBlueprint, type CustomSandboxSize, type RepoAccess } from '@runloop/reflex-contract';
import {
  displayBlueprintName,
  resolveBaseBlueprintNameForCreate,
  BASE_BLUEPRINT_NAME,
  BlueprintArchitectureSchema,
  DEFAULT_SANDBOX_RESOURCE_SIZE,
  DEFAULT_TRIAL_RESOURCE_SIZE,
  isResourceSizeAllowedForTrial,
  type Blueprint as SharedBlueprint,
  type BlueprintArchitecture,
} from '@reflex/shared';
import type { Blueprint } from '@/lib/types';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useSandboxHealth } from '@/hooks/org/useSandboxHealth';
import { splitInheritedFromBase } from './blueprint-create-utils';

const blueprintFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    dockerfile: z.string(),
    userMode: z.enum(['root', 'user']),
    // Nullable so the picker's "(Default)" option and custom-size selection can
    // both clear the named tier. `null` means "use the org/platform default";
    // a selected `customSize` (tracked outside the form) supersedes it on submit.
    resourceSize: z.enum(['SMALL', 'MEDIUM', 'LARGE', 'X_LARGE', 'XX_LARGE']).nullable(),
    repoSlug: z.string(),
    repoAccess: z.enum(['connected', 'public']),
    installCommand: z.string(),
    baseBlueprintName: z.string(),
    architecture: BlueprintArchitectureSchema,
  })
  .refine((d) => d.dockerfile.trim() || d.baseBlueprintName, {
    message: 'Either a Dockerfile or a base blueprint is required',
    path: ['dockerfile'],
  })
  // Runloop's blueprint create endpoint rejects the combination outright
  // (returns 400). The UI mirrors that by clearing the other field whenever
  // one is set, but we keep this refine as a defensive backstop in case some
  // future code path bypasses the onChange hooks.
  .refine((d) => !(d.dockerfile.trim() && d.baseBlueprintName), {
    message:
      'Use either a Dockerfile or a base blueprint, not both. To inherit from a base blueprint inside a Dockerfile, start it with `FROM runloop:base`.',
    path: ['dockerfile'],
  });

export type BlueprintFormValues = z.infer<typeof blueprintFormSchema>;

const BASE_BLUEPRINT_DOCS = 'concepts/blueprints#base-blueprint';
const SANDBOX_SIZE_DOCS = 'concepts/devboxes#devbox-sizes';

const USER_MODE_OPTIONS = [
  { value: 'user' as const, label: 'Run as user', description: 'Commands run as the sandbox user' },
  {
    value: 'root' as const,
    label: 'Run as root',
    description: 'Commands run with root privileges',
  },
];

const ARCHITECTURE_OPTIONS = [
  { value: 'x86_64' as const, label: 'x86_64', description: 'Intel/AMD (default)' },
  { value: 'arm64' as const, label: 'arm64', description: 'Arm 64-bit' },
];

/**
 * Base-blueprint picker rendered as a launch-style config chip. Mirrors the
 * `AdvancedConfigChips` blueprint chip so the create form reads like the launch
 * dialog. Disabled (shown as a static summary) when the new blueprint is itself
 * a base — a base is a root image and can't build on top of another blueprint.
 */
function BaseBlueprintChip({
  baseBlueprints,
  value,
  disabled,
  onChange,
}: {
  baseBlueprints: SharedBlueprint[];
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  // The form's default of `base` refers to the public base in a vanilla org
  // (no per-org base built), so resolve before matching — otherwise the chip
  // reads "Start from scratch" while the form still submits a base.
  const effectiveValue = value ? resolveBaseBlueprintNameForCreate(value, baseBlueprints) : '';
  const selected = effectiveValue
    ? (baseBlueprints.find((bp) => bp.name === effectiveValue) ?? null)
    : null;
  const label = effectiveValue
    ? displayBlueprintName(selected?.name ?? effectiveValue)
    : 'Start from scratch';
  const options: ConfigChipSelectOption<string>[] = [
    {
      key: '__none__',
      value: '',
      label: <span className="text-muted-foreground">Start from scratch</span>,
      searchValue: '__none__ Start from scratch',
    },
    ...baseBlueprints.map((bp) => ({
      key: bp.id,
      value: bp.name,
      label: displayBlueprintName(bp.name),
      searchValue: `${displayBlueprintName(bp.name)} ${bp.id}`,
      icon: <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />,
      group: 'Base blueprints',
    })),
  ];
  return (
    <ConfigChipSelect
      ariaLabel="Base Blueprint"
      disabled={disabled}
      triggerLabel={label}
      triggerIcon={<Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      options={options}
      value={effectiveValue}
      onChange={onChange}
      searchPlaceholder="Search base blueprints…"
      emptyMessage="No base blueprints found."
    />
  );
}

/**
 * CPU-architecture picker rendered as a launch-style config chip. Only offered
 * for from-scratch (Dockerfile) builds; a blueprint built on a base inherits
 * the base's architecture.
 */
function ArchitectureChip({
  value,
  onChange,
}: {
  value: BlueprintArchitecture;
  onChange: (next: BlueprintArchitecture) => void;
}) {
  const options: ConfigChipSelectOption<BlueprintArchitecture>[] = ARCHITECTURE_OPTIONS.map(
    (opt) => ({
      key: opt.value,
      value: opt.value,
      label: opt.label,
      searchValue: `${opt.label} ${opt.description}`,
      trailing: <span className="shrink-0 text-2xs text-muted-foreground">{opt.description}</span>,
    }),
  );
  return (
    <ConfigChipSelect
      ariaLabel="Architecture"
      triggerLabel={value}
      triggerIcon={<Cpu className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      options={options}
      value={value}
      onChange={onChange}
    />
  );
}

/** OS user-mode picker rendered as a launch-style config chip. */
function UserModeChip({
  value,
  onChange,
}: {
  value: 'root' | 'user';
  onChange: (next: 'root' | 'user') => void;
}) {
  const selected = USER_MODE_OPTIONS.find((o) => o.value === value) ?? USER_MODE_OPTIONS[0]!;
  const options: ConfigChipSelectOption<'root' | 'user'>[] = USER_MODE_OPTIONS.map((opt) => ({
    key: opt.value,
    value: opt.value,
    label: opt.label,
    searchValue: opt.label,
    trailing: <span className="shrink-0 text-2xs text-muted-foreground">{opt.description}</span>,
  }));
  return (
    <ConfigChipSelect
      ariaLabel="User Mode"
      triggerLabel={selected.label}
      triggerIcon={<UserCog className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      options={options}
      value={value}
      onChange={onChange}
    />
  );
}

export interface BlueprintCreateFormProps {
  /** Repo to seed the streamlined repo-image flow with. */
  prefillRepo?: BlueprintPrefillRepoState;
  /** Blueprint to pre-fill the full custom editor from (Clone/Edit). */
  cloneSource?: Blueprint;
  /** Called when the user abandons the form. */
  onCancel: () => void;
  /** Called after the blueprint was created. */
  onCreated: () => void;
  /** Surface classes (border, background, max width) supplied by the host. */
  className?: string;
}

/**
 * The blueprint create form as a bounded, self-contained block (its own header,
 * scrolling body, and pinned footer). Hosts supply the surface it sits on and
 * the exit callbacks: `BlueprintCreatePanel` renders it as a card in the
 * blueprints detail area, and `BlueprintCreateDialog` renders it as a modal so
 * the flow can be finished without leaving the current page.
 */
export function BlueprintCreateForm({
  prefillRepo,
  cloneSource,
  onCancel,
  onCreated,
  className,
}: BlueprintCreateFormProps) {
  const { data: blueprints = [] } = useBlueprints();
  const createBlueprint = useCreateBlueprint();
  const { data: baseImage } = useBaseImage();
  const { activeOrganizationId, activeOrganization } = useOrganization();
  const sandboxHealth = useSandboxHealth(activeOrganizationId);
  const isTrial = sandboxHealth.data?.status === 'trial';
  // The org's custom sizes feed the size chip so a blueprint can be built at
  // one, exactly like the agent launcher. The org's *default* size is a
  // launch-time setting and deliberately not passed: a blueprint build has no
  // org-default concept (the request schema defaults to `X_LARGE`), so the
  // chip's "(Default)" option must read as the platform default it will build
  // at, not the org's launch default.
  const orgCustomSizes = activeOrganization?.customSandboxSizes ?? [];

  // The repo-image flow needs a repository, so it has to be able to say when
  // there is no Git provider to pick one from. Read the same query the picker
  // below reads (empty term, `user` scope — the key carries neither, so this
  // shares the picker's cache entry and issues no extra request) and classify
  // it with the same rule the picker uses.
  const githubRepos = useGitHubRepos('', true, 'user');
  const githubConnection = deriveGitHubConnectionState(githubRepos);

  // Surface create failures in a centered, persistent modal rather than a
  // toast — Runloop validation errors (e.g. invalid base-blueprint + dockerfile
  // combos) are easy to miss in a transient toast at the corner of the screen.
  const [createError, setCreateError] = useState<string | null>(null);

  // Two creation modes share this panel. "repo-image" (the default) is a
  // streamlined flow: pick a repo, auto-name the image, and build on the
  // default base. "custom" is the full editor. Clone/Edit always open custom
  // since they pre-fill a specific blueprint's fields.
  const [mode, setMode] = useState<'repo-image' | 'custom'>(cloneSource ? 'custom' : 'repo-image');
  const repoImageMode = mode === 'repo-image';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BlueprintFormValues>({
    resolver: zodResolver(blueprintFormSchema),
    defaultValues: {
      name:
        cloneSource?.name ?? (prefillRepo ? deriveBlueprintNameFromRepo(prefillRepo.repoSlug) : ''),
      dockerfile: cloneSource?.dockerfile ?? '',
      userMode: 'user',
      // A clone built at a custom size clears the named tier (custom wins);
      // `customSize` state below carries the dimensions.
      resourceSize: cloneSource?.customSize
        ? null
        : (cloneSource?.resourceSize ??
          (isTrial ? DEFAULT_TRIAL_RESOURCE_SIZE : DEFAULT_SANDBOX_RESOURCE_SIZE)),
      repoSlug: cloneSource?.metadata?.repo ?? prefillRepo?.repoSlug ?? '',
      repoAccess:
        cloneSource?.metadata?.repoAccess === 'public'
          ? 'public'
          : (prefillRepo?.repoAccess ?? 'connected'),
      installCommand: cloneSource?.installCommand ?? '',
      baseBlueprintName: cloneSource ? (cloneSource.baseBlueprintName ?? '') : BASE_BLUEPRINT_NAME,
      // Prefill only from a from-scratch clone. A derived clone also carries a
      // stamped architecture, but it came from its base; carrying it here
      // would silently pin arm64 if the user detaches the clone to "start
      // from scratch" while the chip sits in a collapsed Advanced section.
      architecture:
        cloneSource && !cloneSource.baseBlueprintName
          ? (cloneSource.architecture ?? 'x86_64')
          : 'x86_64',
    },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- React Hook Form's watch() is safe here; React Compiler compat is a known limitation
  const watchedRepoFullName = watch('repoSlug');
  const watchedRepoAccess = watch('repoAccess');
  const watchedBaseBlueprintName = watch('baseBlueprintName');
  const watchedName = watch('name');
  const watchedResourceSize = watch('resourceSize');
  const watchedDockerfile = watch('dockerfile');

  // Org-defined custom devbox size, tracked outside the RHF form (mirrors the
  // launcher, where the custom size is a separate object rather than a named
  // tier). Prefilled from a clone that pinned one; supersedes `resourceSize`.
  const [customSize, setCustomSize] = useState<CustomSandboxSize | null>(
    () => cloneSource?.customSize ?? null,
  );

  // Health resolves asynchronously, so an existing clone or a user who opened
  // the form before the trial response arrived can still hold an oversized
  // value. Change only that one field and leave all other in-progress edits
  // intact; the server remains authoritative if this UI backstop is stale.
  useEffect(() => {
    if (!isTrial) return;
    // Trials can't use custom sizes, so fall back to the trial default if a
    // clone dropped one in.
    if (customSize) {
      setCustomSize(null);
      setValue('resourceSize', DEFAULT_TRIAL_RESOURCE_SIZE, { shouldValidate: true });
      return;
    }
    // Only a named tier can exceed the cap; `null` (org default) is left alone.
    if (watchedResourceSize && !isResourceSizeAllowedForTrial(watchedResourceSize)) {
      setValue('resourceSize', DEFAULT_TRIAL_RESOURCE_SIZE, { shouldValidate: true });
    }
  }, [isTrial, setValue, watchedResourceSize, customSize]);

  // A base blueprint is a root image — it can't be built on top of another
  // blueprint. We treat the new blueprint as a base when EITHER the clone
  // source was itself a base (so editing a base and rebuilding keeps it a
  // base, regardless of the new name) OR the name being typed marks it as a
  // base via the canonical `isBaseBlueprint` rule.
  // The repo-image flow always builds ON the base and never creates one, so
  // the name-based rule only applies in the custom editor — where the Base
  // Blueprint chip that it disables actually exists.
  const cloneSourceIsBase = cloneSource ? isBaseBlueprint(cloneSource) : false;
  const buildingBase =
    !repoImageMode &&
    (cloneSourceIsBase ||
      isBaseBlueprint({ name: watchedName?.trim() ?? '', metadata: cloneSource?.metadata }));

  // Spread `register('name')` / `register('dockerfile')` once so the JSX can
  // wrap their onChange without re-creating the registration on every render.
  const nameRegister = register('name');
  // Once the user types a name of their own, repo selection stops overwriting
  // it. Without this the repo-image flow would silently clobber a name the
  // user chose before picking (or changing) the repo.
  const nameEditedRef = useRef(false);
  const dockerfileRegister = register('dockerfile');

  // `setupCommands` holds ONLY the user-editable extras. The base-managed
  // prefix is derived from `baseImage.mergedSetupCommands` and rendered
  // as locked rows above it. We snapshot the clone source on first
  // render so a late base-image fetch doesn't yank commands out from
  // under the user mid-edit.
  const cloneSourceCommandsRef = useRef<string[]>(cloneSource?.systemSetupCommands ?? []);
  const [setupCommands, setSetupCommands] = useState<string[]>(
    () => cloneSourceCommandsRef.current,
  );
  // Resplit once the base-image data arrives. We can't do this during
  // the `useState` initializer because the query is async; instead we
  // resync state during render (React's "Adjusting state on prop
  // change" pattern) the first time we see a baseImage chain that
  // proves the clone source had inherited commands baked in.
  const [appliedBaseChainKey, setAppliedBaseChainKey] = useState<string | null>(null);
  const baseChain = baseImage?.mergedSetupCommands;
  const baseChainKey = baseChain ? baseChain.join('\n') : null;
  if (baseChainKey && baseChainKey !== appliedBaseChainKey) {
    const { extras } = splitInheritedFromBase(cloneSourceCommandsRef.current, baseChain);
    setAppliedBaseChainKey(baseChainKey);
    if (extras.length !== setupCommands.length || extras.some((c, i) => c !== setupCommands[i])) {
      setSetupCommands(extras);
    }
  }
  const inheritedCommands: string[] = baseChain
    ? splitInheritedFromBase(cloneSourceCommandsRef.current, baseChain).inherited
    : [];
  const [repoBranch, setRepoBranch] = useState(
    () => cloneSource?.repoBranch ?? prefillRepo?.repoBranch ?? '',
  );
  const [newCommand, setNewCommand] = useState('');
  // Launch commands map to Runloop `launch_parameters.launch_commands` —
  // shell commands run on every devbox boot before the agent process
  // starts. They are not baked into the image like systemSetupCommands,
  // so they don't participate in base-blueprint inheritance.
  const [launchCommands, setLaunchCommands] = useState<string[]>(
    () => cloneSource?.launchCommands ?? [],
  );
  const [newLaunchCommand, setNewLaunchCommand] = useState('');
  const [fileMounts, setFileMounts] = useState<KeyValueEntry[]>(() => {
    if (cloneSource?.fileMounts) {
      return Object.entries(cloneSource.fileMounts).map(([key, value]) => ({ key, value }));
    }
    return [];
  });
  const [buildArgs, setBuildArgs] = useState<KeyValueEntry[]>(() => {
    if (cloneSource?.buildArgs) {
      return Object.entries(cloneSource.buildArgs).map(([key, value]) => ({ key, value }));
    }
    return [];
  });
  // Build secrets round-trip through the read-side `Blueprint` shape: the
  // server reverse-maps each stored Runloop secret name back to its friendly
  // `{envVar, secretRef}` ref so editing or rebuilding preserves them. Runloop
  // still strips the secret VALUES from the image; only the binding is shown.
  const [buildSecrets, setBuildSecrets] = useState<BlueprintBuildSecret[]>(
    () => cloneSource?.buildSecrets ?? [],
  );
  const fileMountsRef = useRef<FileMountsEditorHandle>(null);
  const buildArgsRef = useRef<KeyValueEditorHandle>(null);
  const [advancedOpen, setAdvancedOpen] = useState(
    () =>
      (cloneSource?.systemSetupCommands?.length ?? 0) > 0 ||
      (cloneSource?.launchCommands?.length ?? 0) > 0 ||
      Object.keys(cloneSource?.fileMounts ?? {}).length > 0 ||
      Object.keys(cloneSource?.buildArgs ?? {}).length > 0 ||
      (cloneSource?.buildSecrets?.length ?? 0) > 0 ||
      // A from-scratch arm64 clone re-submits its architecture, so surface it.
      (cloneSource?.architecture === 'arm64' && !cloneSource?.baseBlueprintName),
  );
  const [dockerfileOpen, setDockerfileOpen] = useState(true);
  const [repoOpen, setRepoOpen] = useState(true);

  const baseBlueprints = useMemo(() => {
    // Aligned with the sidebar's "Base" section: the canonical
    // `isBaseBlueprint` rule (metadata.type='base' OR name 'base' OR
    // name contains '_base'). Only completed builds are valid as a
    // parent — building/failed ones can't be inherited from.
    const candidates = blueprints.filter(
      (bp) => bp.status === 'build_complete' && isBaseBlueprint(bp),
    );
    return getLatestByName(candidates);
  }, [blueprints]);

  function onSubmit(data: BlueprintFormValues) {
    // Concatenate inherited (locked, base-managed) + editable extras +
    // any pending input still in the add-command field. The persisted
    // blueprint must carry the full chain to match what the user sees
    // and what Runloop bakes into the image.
    const editableExtras = newCommand.trim()
      ? [...setupCommands, newCommand.trim()]
      : setupCommands;
    const allCommands = [...inheritedCommands, ...editableExtras];

    const allLaunchCommands = newLaunchCommand.trim()
      ? [...launchCommands, newLaunchCommand.trim()]
      : launchCommands;

    const pendingFileMount = fileMountsRef.current?.getPending();
    const allFileMounts = pendingFileMount ? [...fileMounts, pendingFileMount] : fileMounts;
    // Build args only apply to a from-scratch (Dockerfile) build. When a base
    // blueprint is selected the Docker build is skipped, so we drop any build
    // args entirely — including ones left over from before a base was picked.
    const pendingBuildArg = data.baseBlueprintName ? null : buildArgsRef.current?.getPending();
    const allBuildArgs = data.baseBlueprintName
      ? []
      : pendingBuildArg
        ? [...buildArgs, pendingBuildArg]
        : buildArgs;

    const fileMountsRecord =
      allFileMounts.length > 0
        ? Object.fromEntries(allFileMounts.map((e) => [e.key, e.value]))
        : undefined;
    const buildArgsRecord =
      allBuildArgs.length > 0
        ? Object.fromEntries(allBuildArgs.map((e) => [e.key, e.value]))
        : undefined;

    // Drop blank rows so a half-filled editor row doesn't 400 the create
    // call (`envVar` has a strict uppercase format on the server side).
    const cleanBuildSecrets = buildSecrets.filter(
      (row) => row.envVar.trim() && row.secretRef.trim(),
    );
    // A custom size supersedes the named tier — the server maps it onto
    // Runloop's CUSTOM_SIZE and ignores `resourceSize`. Trials can't use custom
    // sizes, so drop it there. `resourceSize` is always sent (the request
    // requires it): a null "(Default)" pick falls back to the platform default,
    // and an oversized named tier is clamped down to the trial cap.
    const effectiveCustomSize = isTrial ? null : customSize;
    const namedSize = data.resourceSize ?? DEFAULT_SANDBOX_RESOURCE_SIZE;
    const resourceSize =
      isTrial && !isResourceSizeAllowedForTrial(namedSize)
        ? DEFAULT_TRIAL_RESOURCE_SIZE
        : namedSize;

    createBlueprint.mutate(
      {
        name: data.name.trim(),
        dockerfile: data.dockerfile.trim(),
        userMode: data.userMode,
        resourceSize,
        ...(effectiveCustomSize ? { customSize: effectiveCustomSize } : {}),
        // Architecture only applies to a from-scratch build (a derived
        // blueprint inherits its base's architecture), and it rides the
        // request only when it departs from the x86_64 default.
        ...(!data.baseBlueprintName && data.architecture !== 'x86_64'
          ? { architecture: data.architecture }
          : {}),
        ...(data.repoSlug ? { repoSlug: data.repoSlug } : {}),
        ...(data.repoSlug ? { repoAccess: data.repoAccess } : {}),
        ...(data.repoSlug && repoBranch.trim() ? { repoBranch: repoBranch.trim() } : {}),
        ...(data.repoSlug && data.installCommand.trim()
          ? { installCommand: data.installCommand.trim() }
          : {}),
        ...(allCommands.length > 0 ? { systemSetupCommands: allCommands } : {}),
        ...(allLaunchCommands.length > 0 ? { launchCommands: allLaunchCommands } : {}),
        // Resolve the default `base` against what actually exists: a vanilla
        // org has no per-org base build, so its base is the shared public one.
        ...(data.baseBlueprintName
          ? {
              baseBlueprintName: resolveBaseBlueprintNameForCreate(
                data.baseBlueprintName,
                baseBlueprints,
              ),
            }
          : {}),
        ...(fileMountsRecord ? { fileMounts: fileMountsRecord } : {}),
        ...(buildArgsRecord ? { buildArgs: buildArgsRecord } : {}),
        ...(cleanBuildSecrets.length > 0 ? { buildSecrets: cleanBuildSecrets } : {}),
        ...(buildingBase ? { isBase: true } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Blueprint created');
          onCreated();
        },
        onError: (err: Error) => setCreateError(err.message || 'Unknown error'),
      },
    );
  }

  // The two modes have different contracts. The repo-image flow builds an image
  // around a repository, so it needs one; the custom editor builds a generic
  // image, where everything but the name has a working default (`repoSlug` is
  // optional on `POST /blueprints`, and with no repo the build just uses the
  // base blueprint).
  const hasName = Boolean(watchedName?.trim());
  const hasRepo = Boolean(watchedRepoFullName?.trim());
  const canSubmit = repoImageMode ? hasName && hasRepo : hasName;
  // The Dockerfile-vs-base refines are the only way a submittable form can
  // still fail validation, and their message is pinned to a field that exists
  // in one mode only. Surface it next to the button in both — but only while
  // the button is actually pressable, so clearing a gated field afterwards
  // can't replace a real validation failure with a gate prompt.
  const blockedMessage = canSubmit ? errors.dockerfile?.message : undefined;

  // `isBaseBlueprint` classifies by name alone, so a repo-image build called
  // `foo_base` is listed under Base blueprints and offered as a parent even
  // though it is derived and carries no `isBase` stamp. That is cosmetic, not
  // corrupting, so warn instead of blocking. Custom mode is exempt: there a
  // base-like name really does build a base (`buildingBase` stamps it).
  const nameLooksLikeBase = repoImageMode && isBaseBlueprint({ name: watchedName?.trim() ?? '' });

  function handleRepoChange(
    slug: string,
    options: { access?: RepoAccess } | undefined,
    onChange: (next: string) => void,
  ) {
    onChange(slug);
    setValue('repoAccess', options?.access ?? 'connected', {
      shouldValidate: true,
      shouldDirty: true,
    });
    // In the repo-image flow the name is auto-derived from the repo, so keep it
    // in sync as the selection changes — until the user names it themselves.
    if (repoImageMode && !nameEditedRef.current) {
      setValue('name', slug ? deriveBlueprintNameFromRepo(slug) : '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }

  // Sections shared by both modes are built once here and composed differently
  // per mode below, so the field components and their wiring stay single-sourced.
  const sandboxSizeChip = (
    <ConfigChipField label="Sandbox Size" docsSlug={SANDBOX_SIZE_DOCS}>
      <Controller
        name="resourceSize"
        control={control}
        render={({ field }) => (
          <DevboxSizeChip
            ariaLabel="Sandbox Size"
            value={field.value}
            onChange={field.onChange}
            customSize={customSize}
            onCustomSizeChange={setCustomSize}
            customSizes={orgCustomSizes}
            isTrial={isTrial}
          />
        )}
      />
    </ConfigChipField>
  );

  // Name is the one genuinely required field in both modes. The repo-image
  // flow keeps it filled for you (derived from the repo), but it stays
  // editable so an image with no repository — the only option when no Git
  // provider is connected — can still be named and built.
  const nameSection = (
    <FormField label="Name" htmlFor="blueprint-create-name" error={errors.name?.message}>
      <Input
        // Remount across a mode switch so the custom editor's `autoFocus`
        // fires; the same element in both branches would reconcile in place.
        key={repoImageMode ? 'repo-image' : 'custom'}
        id="blueprint-create-name"
        {...nameRegister}
        onChange={(e) => {
          // Clearing the field hands naming back to the repo, so this tracks
          // "the user has a name of their own", not "has ever typed".
          nameEditedRef.current = e.target.value.trim().length > 0;
          void nameRegister.onChange(e);
          // Renaming into a base name (e.g. `node_base`) makes the
          // base-blueprint selector invalid; drop any prior selection
          // so we can't submit a base built on a base. Custom mode only:
          // the repo-image flow has no selector to correct it with, so
          // clearing the base there would strand the form with neither a
          // base nor a Dockerfile.
          if (
            !repoImageMode &&
            watchedBaseBlueprintName &&
            isBaseBlueprint({
              name: e.target.value.trim(),
              metadata: cloneSource?.metadata,
            })
          ) {
            setValue('baseBlueprintName', '', {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }}
        placeholder="my-blueprint"
        autoFocus={!repoImageMode}
      />
      {nameLooksLikeBase && (
        <p className="pl-0.5 text-2xs text-status-warning">
          Names containing <span className="font-mono">_base</span> are listed as base images. This
          one builds on your base, so pick a different name unless you want it listed there.
        </p>
      )}
    </FormField>
  );

  const repositorySection = (
    <CollapsibleConfigSection
      id="blueprint-repository-section"
      title="Repository"
      labelClassName="text-2xs"
      docsSlug="concepts/blueprints#repository"
      open={repoOpen}
      onToggle={() => setRepoOpen((v) => !v)}
    >
      <div className="space-y-3">
        <p className="pl-0.5 text-2xs text-muted-foreground">
          {repoImageMode
            ? 'Required. Reflex clones this repo into the image at build time, so agents launched from this blueprint start with it already checked out. Public repos work without a GitHub connection.'
            : 'Optional. A repository is cloned into the image at build time, so agents launched from this blueprint start with it already checked out. Leave it empty to build without one.'}
        </p>
        <div data-tutorial-id="blueprint-repo">
          <Controller
            name="repoSlug"
            control={control}
            render={({ field }) => (
              <RepoSelector
                repoSlug={field.value}
                branch={repoBranch}
                repoAccess={watchedRepoAccess}
                allowPublicRepos
                // Default `user` scope: the build clones with the creating
                // user's own OAuth token, so the search has to list exactly
                // what that credential can reach. (The org App installation
                // remains the server-side fallback.)
                onRepoChange={(slug, options) => handleRepoChange(slug, options, field.onChange)}
                onBranchChange={setRepoBranch}
              />
            )}
          />
          {watchedRepoAccess === 'public' && watchedRepoFullName && (
            <p className="mt-1.5 pl-0.5 text-2xs text-muted-foreground">
              Will mount <span className="font-mono text-foreground">{watchedRepoFullName}</span> as
              a public repo, without requiring a GitHub App installation.
            </p>
          )}
        </div>
        {/* The repo-image flow can't be finished without a repository, so it has
            to say so when there is no Git provider to pick one from. This sits
            BESIDE the picker, never in place of it: typing an `owner/name` into
            the picker mounts a public repo, which is the one way to satisfy
            this flow with no GitHub connection at all. */}
        {repoImageMode && !hasRepo && githubConnection.needsReconnect && (
          <div
            className="space-y-1.5 rounded-md border border-border bg-muted/20 p-3"
            data-testid="blueprint-github-connect-prompt"
          >
            <p className="text-xs font-medium">
              {githubConnection.tokenMissing
                ? 'GitHub access token expired'
                : 'GitHub is not connected'}
            </p>
            <p className="text-2xs text-muted-foreground">
              {githubConnection.tokenMissing
                ? 'Reconnect to refresh your access token and browse your repos.'
                : 'Connect your GitHub account to browse your repos.'}{' '}
              To build from a public repo, type its owner/name in the picker instead. To build an
              image with no repo at all, create a custom blueprint.
            </p>
            <Button asChild size="sm" variant="outline" className="mt-1 text-xs">
              <a href={getApiUrl('/auth/github')}>
                <Github className="icon-sm" />
                {githubConnection.tokenMissing ? 'Reconnect GitHub' : 'Connect GitHub'}
              </a>
            </Button>
          </div>
        )}
        {watchedRepoFullName && (
          <FieldGroup
            label="Install Command"
            docsSlug="concepts/blueprints#install-command"
            hint="Command to install dependencies after cloning the repository"
          >
            <Controller
              name="installCommand"
              control={control}
              render={({ field }) => (
                <EditableCommand
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="npm install"
                  ariaLabel="Install command"
                />
              )}
            />
          </FieldGroup>
        )}
      </div>
    </CollapsibleConfigSection>
  );

  const systemSetupSection = (
    <FieldGroup
      label="System Setup Commands"
      docsSlug="concepts/blueprints#system-setup-commands"
      hint="Commands run once during the blueprint build and baked into the image."
    >
      {inheritedCommands.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Lock className="h-2.5 w-2.5" />
            <SectionLabel as="span">
              From base image · {inheritedCommands.length} commands
            </SectionLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                These commands are baked into the host-managed base blueprint (core + plugin
                contributions + organization overrides) and cannot be edited here. Manage them under
                Blueprints → base.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="space-y-1">
            {inheritedCommands.map((cmd, i) => (
              <div
                key={`inherited-${i}`}
                className="flex items-center gap-2 rounded border border-dashed border-border bg-muted/10 px-2 py-1.5 text-sm font-mono text-muted-foreground"
                data-readonly="true"
              >
                <Lock className="h-3 w-3 shrink-0 opacity-60" />
                <span className="flex-1 truncate">{cmd}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {inheritedCommands.length > 0 && (
        <div className="flex items-center gap-1.5 pt-1 text-muted-foreground">
          <Plus className="h-2.5 w-2.5" />
          <SectionLabel as="span">Blueprint extras</SectionLabel>
        </div>
      )}
      <EditableCommandList
        commands={setupCommands}
        onCommandsChange={setSetupCommands}
        draft={newCommand}
        onDraftChange={setNewCommand}
        placeholder="apt-get install -y curl"
      />
    </FieldGroup>
  );

  const advancedSection = (
    <CollapsibleConfigSection
      id="blueprint-advanced-section"
      title="Advanced"
      labelClassName="text-2xs"
      open={advancedOpen}
      onToggle={() => setAdvancedOpen((v) => !v)}
    >
      <div className="space-y-5">
        {/* User Mode */}
        <ConfigChipField label="User Mode" docsSlug="concepts/blueprints#user-mode">
          <Controller
            name="userMode"
            control={control}
            render={({ field }) => <UserModeChip value={field.value} onChange={field.onChange} />}
          />
        </ConfigChipField>

        {/* Architecture — only a from-scratch (Dockerfile) build gets to pick
            its CPU architecture. A blueprint built on a base inherits the
            base's architecture (Runloop stamps it from the parent), so the
            chip is hidden whenever a base blueprint is selected — same
            treatment as Build Arguments below. */}
        {!watchedBaseBlueprintName && (
          <ConfigChipField label="Architecture" docsSlug="concepts/blueprints#architecture">
            <Controller
              name="architecture"
              control={control}
              render={({ field }) => (
                <ArchitectureChip value={field.value} onChange={field.onChange} />
              )}
            />
          </ConfigChipField>
        )}

        {/* Launch Commands */}
        <div data-tutorial-id="blueprint-launch-commands">
          <FieldGroup
            label="Launch Commands"
            docsSlug="concepts/blueprints#launch-commands"
            hint="Commands to run on every devbox boot, before the agent process starts."
          >
            <EditableCommandList
              commands={launchCommands}
              onCommandsChange={setLaunchCommands}
              draft={newLaunchCommand}
              onDraftChange={setNewLaunchCommand}
              placeholder="./start.sh"
              addLabel="Add launch command"
              removeLabel="Remove launch command"
            />
          </FieldGroup>
        </div>

        {/* File Mounts */}
        <FieldGroup
          label="File Mounts"
          docsSlug="concepts/blueprints#file-mounts"
          hint="For small config files only (path → content), not large assets or binaries."
        >
          <FileMountsEditor
            ref={fileMountsRef}
            entries={fileMounts}
            onChange={setFileMounts}
            pathPlaceholder="/path/to/file"
            contentPlaceholder="Paste file content here..."
          />
        </FieldGroup>

        {/* Build Secrets — env vars sourced from the org's stored secrets and
            scoped to the build phase only (Runloop strips them from the image).
            Available regardless of base-vs-Dockerfile mode because both can
            run `system_setup_commands` that need credentials. */}
        <FieldGroup
          label="Build Secrets"
          docsSlug="concepts/blueprints#build-secrets"
          hint="Org secrets exposed to the build as environment variables. Not baked into the image."
        >
          <BlueprintBuildSecretsEditor value={buildSecrets} onChange={setBuildSecrets} />
        </FieldGroup>

        {/* Build Args — only meaningful when building from a Dockerfile
            (`docker build --build-arg`). Selecting a base blueprint skips the
            Docker build entirely, so we hide them whenever a base is chosen
            (i.e. not "Start from scratch"). */}
        {!watchedBaseBlueprintName && (
          <FieldGroup
            label="Build Arguments"
            docsSlug="concepts/blueprints#build-arguments"
            hint="Arguments to pass to docker build (--build-arg)"
          >
            <KeyValueEditor
              ref={buildArgsRef}
              entries={buildArgs}
              onChange={setBuildArgs}
              keyPlaceholder="ARG_NAME"
              valuePlaceholder="value"
              entryLabel="argument"
            />
          </FieldGroup>
        )}
      </div>
    </CollapsibleConfigSection>
  );

  return (
    // A bounded "window" (like the launch-agent dialog): the form fills the
    // height it is given, with the footer pinned to the bottom and the body
    // scrolling internally, so it never leaves an empty band below the footer.
    // Only the structural classes live here; the host owns the surface.
    <div className={cn('flex h-full min-h-0 w-full flex-col overflow-hidden', className)}>
      <div className="shrink-0 border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
          <PanelTitle as="h2" size="sm" docs="concepts/blueprints">
            {repoImageMode ? 'New Repo Image' : 'Custom Blueprint'}
          </PanelTitle>
        </div>
        <PageDescription className="mt-1 text-xs">
          {repoImageMode
            ? 'Reflex builds an image on your base blueprint with a repo cloned in, ready for agents to launch. To build an image without a repo, create a custom blueprint.'
            : 'Configure a prebuilt devbox image (packages, repository, and setup commands) that agents launch from.'}
        </PageDescription>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="p-4 md:p-6">
          <form id="blueprint-create-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {repoImageMode ? (
              <>
                {/* Repository first — it is what the flow is built around, it
                    gates the submit, and picking one names the image for you,
                    so asking for a name above it would invite work the next
                    field undoes. */}
                {repositorySection}
                {nameSection}
                {/* Only once the form can actually be submitted: promising a
                    build next to a button that refuses to run one is the kind
                    of contradiction the footer message exists to remove. */}
                {watchedName && hasRepo && (
                  <p className="pl-0.5 text-2xs text-muted-foreground">
                    Will be created as{' '}
                    <span className="font-mono text-foreground">{watchedName}</span>, built on your{' '}
                    <span className="font-mono text-foreground">base</span> blueprint.
                  </p>
                )}
                <div className="flex flex-wrap items-start gap-x-4 gap-y-3">{sandboxSizeChip}</div>
                {systemSetupSection}
                {advancedSection}
                <div className="border-t border-border/60 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    data-tutorial-id="blueprint-create-custom"
                    onClick={() => setMode('custom')}
                  >
                    <Wrench className="icon-sm" />
                    Create a custom blueprint
                  </Button>
                  <p className="mt-1.5 pl-0.5 text-2xs text-muted-foreground">
                    No repo to bake in, or need a Dockerfile, build arguments, or a different base?
                    Switch to the full editor, where the repository is optional.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Hidden once a Dockerfile has been written: going back clears
                    it (repo-image mode always builds on the base and renders no
                    Dockerfile field), and silently destroying typed work on a
                    one-click link is worse than making the user switch modes
                    with the button below. */}
                {!cloneSource && !watchedDockerfile.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      // Repo-image mode always builds ON the base, so restore
                      // that on the way back. Without it, someone who picked
                      // "Start from scratch" in the custom editor returns to a
                      // form with neither a base nor a Dockerfile, where every
                      // submit fails a validation this mode cannot show a
                      // field for. The Dockerfile is empty here by the guard
                      // above, so there is nothing to clear.
                      setValue('baseBlueprintName', BASE_BLUEPRINT_NAME, {
                        shouldValidate: true,
                      });
                      setMode('repo-image');
                    }}
                    className="-mt-1 flex items-center gap-1 text-2xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Back to repo image
                  </button>
                )}

                {/* Name — the primary field, kept prominent at the top. */}
                {nameSection}

                {/* Primary config — chips, mirroring the launch dialog's settings
                      row. Top-aligned so the chips line up even though Base
                      Blueprint carries an extra helper line below it. */}
                <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
                  {baseBlueprints.length > 0 && (
                    <ConfigChipField label="Base Blueprint" docsSlug={BASE_BLUEPRINT_DOCS}>
                      <Controller
                        name="baseBlueprintName"
                        control={control}
                        render={({ field }) => (
                          <BaseBlueprintChip
                            baseBlueprints={baseBlueprints}
                            value={field.value}
                            disabled={buildingBase}
                            onChange={(next) => {
                              field.onChange(next);
                              // Base blueprint and Dockerfile are mutually
                              // exclusive on the Runloop side (see schema refine).
                              // Clear the Dockerfile when a base is selected so the
                              // user can't reach an invalid state by clicking
                              // through the form in the "wrong" order.
                              if (next) {
                                setValue('dockerfile', '', {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }}
                          />
                        )}
                      />
                      {buildingBase && (
                        <p className="pl-0.5 text-2xs text-muted-foreground">
                          A base is a root image and cannot build on another blueprint
                        </p>
                      )}
                    </ConfigChipField>
                  )}

                  {sandboxSizeChip}
                </div>

                {/* Dockerfile — only shown when "Start from scratch" is selected. */}
                {!watchedBaseBlueprintName && (
                  <CollapsibleConfigSection
                    id="blueprint-dockerfile-section"
                    title="Dockerfile"
                    labelClassName="text-2xs"
                    docsSlug="concepts/blueprints#dockerfile"
                    open={dockerfileOpen}
                    onToggle={() => setDockerfileOpen((v) => !v)}
                  >
                    <div className="space-y-2">
                      <p className="pl-0.5 text-2xs text-muted-foreground">
                        The Dockerfile used to build the blueprint image.
                      </p>
                      <Textarea
                        {...dockerfileRegister}
                        data-tutorial-id="blueprint-dockerfile"
                        placeholder={
                          'FROM ubuntu:22.04\n\nRUN apt-get update && apt-get install -y ...'
                        }
                        className="min-h-[320px] resize-y rounded-lg font-mono"
                      />
                      {errors.dockerfile && (
                        <p className="text-xs text-destructive">{errors.dockerfile.message}</p>
                      )}
                    </div>
                  </CollapsibleConfigSection>
                )}

                {repositorySection}
                {systemSetupSection}
                {advancedSection}
              </>
            )}
          </form>
        </div>
      </div>

      {/* Footer actions, pinned to the bottom of the window — mirrors the
            launch-agent dialog footer (helper text left, actions right). */}
      <div className="flex shrink-0 flex-row items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-3">
        {/* A blocked submit must never be silent. The Dockerfile-vs-base
            refine reports on `dockerfile`, a field the repo-image flow does
            not render, so the message is repeated here where the button the
            user just pressed can be seen. */}
        {blockedMessage ? (
          <span className="mr-auto text-2xs text-destructive" role="alert">
            {blockedMessage}
          </span>
        ) : (
          <span className="mr-auto text-2xs text-muted-foreground">
            {/* Repo first: in the repo-image flow picking one names the image
                for you, so asking for the name first would be asking for work
                the next step undoes. */}
            {repoImageMode && !hasRepo
              ? 'Pick a repository to build the image from.'
              : hasName
                ? 'Builds an image you can launch agents from.'
                : 'Name the image to continue.'}
          </span>
        )}
        <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          className="text-xs"
          form="blueprint-create-form"
          data-tutorial-id="blueprint-create-submit"
          disabled={createBlueprint.isPending || !canSubmit}
        >
          {createBlueprint.isPending ? (
            <>
              <Spinner className="size-4" />
              Creating...
            </>
          ) : (
            'Create Blueprint'
          )}
        </Button>
      </div>

      {/* Portals to the body, so nesting it in the card is purely for
            colocation with the mutation that sets the error. */}
      <AlertDialog
        open={createError !== null}
        onOpenChange={(open) => {
          if (!open) setCreateError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Failed to create blueprint
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground whitespace-pre-wrap break-words">
              {createError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCreateError(null)}>Dismiss</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
